import { useEffect, useState } from "preact/hooks";
import { plural, styled, ToucanButton, writable } from "toucan";
import { importUrls, unsettledItems } from "../model.ts";
import { go } from "../routes.ts";
import { store } from "../state.ts";
import { useSweep } from "../sweep.ts";
import type { ImportOutcome } from "../model.ts";
import type { SweepState } from "../sweep.ts";

/** The header's Save submits this form. */
export const FORM_ID = "import-form";

/**
 * Whether the form is on the page for the header's Save to press: it is
 * until the paste goes in, and the screen after that has nothing to save.
 */
export const importForm = writable(true);

const Page = styled("section")`
  display: grid;
  align-content: start;
  gap: 0.9rem;
  min-height: 100%;
  padding: 1rem;
  /* The same warm canvas the library sits on. */
  background: var(--toucan-canvas);

  textarea {
    margin: 0;
    /* Tall enough that a tab dump reads as a list rather than as a slot. */
    min-height: 12rem;
    font-family: var(--pico-font-family-monospace);
    font-size: 0.9rem;
  }

  /* What goes in the field, under it and flush with it. */
  .hint {
    margin: 0.5rem 0 0;
    color: var(--toucan-muted);
  }

  /* The lines that were not addresses, kept as they were pasted so whoever
     pasted them can see which ones to go back for. */
  .rejected {
    margin: 0;
    padding-left: 1.2rem;
    color: var(--toucan-danger-text);
    font-family: var(--pico-font-family-monospace);
    font-size: 0.85rem;
    overflow-wrap: anywhere;
  }
`;

/**
 * How far the sweep has got with the paste's own links, in one line. `left`
 * is how many of them it has yet to look up. It says when the sweep has
 * stalled on a host that won't answer, since a countdown alone would sit
 * unchanged over a sweep that stopped.
 */
function Progress({ state, left }: { state: SweepState; left: number }) {
  if (left === 0) {
    return <p class="muted">Everything saved has been looked up.</p>;
  }
  if (state === "stalled") {
    return (
      <p class="muted" aria-live="polite">
        {plural(left, "link", "links")} could not be looked up — refused, or
        this device is offline. They are saved, and Reads will try them again.
      </p>
    );
  }
  return (
    <p class="muted" aria-live="polite">
      Finding out what {plural(left, "link", "links")}{" "}
      {left === 1 ? "is" : "are"}…
    </p>
  );
}

/** What the import did, and how far the sweep behind it has got. */
function Outcome({
  outcome,
  left,
  state,
}: {
  outcome: ImportOutcome;
  left: number;
  state: SweepState;
}) {
  return (
    <>
      <p>
        {plural(outcome.saved, "link")} saved
        {outcome.already > 0 && `, ${String(outcome.already)} already here`}.
      </p>
      <Progress state={state} left={left} />
      {outcome.rejected.length > 0 && (
        <>
          <p>
            {plural(outcome.rejected.length, "line")}{" "}
            {outcome.rejected.length === 1 ? "was" : "were"} not a web address:
          </p>
          <ul class="rejected">
            {outcome.rejected.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * Imports a bulk paste of addresses one to a line (as browsers export tabs)
 * or of text with addresses in it, such as a newsletter. Each line with no
 * address is reported back. The lookups afterwards are the library's own
 * sweep, with a progress line.
 */
export function ImportPage() {
  const [text, setText] = useState("");
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  // The sweep is the library's, so only the paste's links count here.
  const left =
    outcome === null
      ? 0
      : unsettledItems(store()).filter((item) => outcome.ids.includes(item.id))
          .length;
  const state = useSweep(outcome !== null);
  // The header's Save is the shell's, and it presses a form this page takes
  // off the screen once the paste is in.
  useEffect(() => {
    importForm.set(outcome === null);
    return () => {
      importForm.set(true);
    };
  }, [outcome]);
  return (
    <Page className="page-column">
      {outcome === null ? (
        <form
          id={FORM_ID}
          onSubmit={(event) => {
            event.preventDefault();
            // One clock reading for the whole paste, so which items compare
            // equal does not depend on the speed of the machine.
            setOutcome(importUrls(store(), text, Date.now()));
          }}
        >
          <label>
            Text with links in it
            <textarea
              rows={12}
              placeholder={"https://…\nhttps://…"}
              value={text}
              autocapitalize="off"
              autocorrect="off"
              spellcheck={false}
              onInput={(event) => {
                setText(event.currentTarget.value);
              }}
            />
          </label>
          <p class="hint">
            Enter a link on each line, or paste text with links in it.
          </p>
        </form>
      ) : (
        <>
          <Outcome outcome={outcome} left={left} state={state} />
          <div>
            <ToucanButton
              variant="primary"
              onClick={() => {
                go("");
              }}
            >
              Back to the library
            </ToucanButton>
          </div>
        </>
      )}
    </Page>
  );
}
